/**
 * Swimlane grouping utilities for GanttView
 */

import { sortTasks } from '../../components/SortDropdown';

// Priority config for swimlanes
export const PRIORITY_CONFIG = [
  { id: 'high', name: 'High', color: '#ef4444' },
  { id: 'medium', name: 'Medium', color: '#f59e0b' },
  { id: 'low', name: 'Low', color: '#22c55e' },
  { id: null, name: 'No Priority', color: '#6b7280' },
];

// Default color
export const DEFAULT_COLOR = '#6b7280';

/**
 * Get primary tag from task
 * Handles different tag formats (array of strings, array of objects, category)
 */
export const getPrimaryTag = (task) => {
  if (Array.isArray(task.tags) && task.tags.length > 0) {
    const firstTag = task.tags[0];
    return typeof firstTag === 'string' ? firstTag : firstTag?.name;
  }
  if (task.category) {
    return typeof task.category === 'string' ? task.category : task.category?.name;
  }
  return null;
};

/**
 * Get bar color based on colorBy setting
 */
export const getBarColor = (task, colorBy, statuses, allTags) => {
  switch (colorBy) {
    case 'status': {
      const statusConfig = statuses.find(s => s.id === task.status) || {};
      return statusConfig.color || DEFAULT_COLOR;
    }
    case 'priority': {
      const priorityConfig = PRIORITY_CONFIG.find(p => p.id === task.priority);
      return priorityConfig?.color || DEFAULT_COLOR;
    }
    case 'tag': {
      const primaryTagName = getPrimaryTag(task);
      if (!primaryTagName) return DEFAULT_COLOR;
      const tagConfig = allTags.find(t => t.name === primaryTagName);
      return tagConfig?.color || DEFAULT_COLOR;
    }
    default:
      return DEFAULT_COLOR;
  }
};

/**
 * Group tasks by the swimlane setting
 * Returns an array of groups, each with key, label, color, and tasks
 * 
 * When swimlaneSetting is 'none', returns null to indicate no grouping (flat list)
 * 
 * @param {Array} tasks - Array of task objects
 * @param {string} swimlaneSetting - Swimlane grouping setting ('none', 'status', 'priority', 'tag', 'difficulty')
 * @param {Array} statuses - Array of status configurations
 * @param {Array} allTags - Array of tag configurations
 * @param {string} sortSetting - Sort setting to apply within each group ('manual', 'priority-high', etc.)
 * @param {Array} customFields - Custom field definitions (for custom field sorting)
 */
export const groupTasksByColorBy = (tasks, swimlaneSetting, statuses, allTags, sortSetting = 'manual', customFields = []) => {
  // No grouping - return null to indicate flat list
  if (swimlaneSetting === 'none' || !swimlaneSetting) {
    return null;
  }
  
  const groups = new Map();
  
  tasks.forEach(task => {
    let key, label, color;
    
    switch (swimlaneSetting) {
      case 'status': {
        const statusConfig = statuses.find(s => s.id === task.status);
        key = task.status || '__none__';
        label = statusConfig?.name || 'No Status';
        color = statusConfig?.color || DEFAULT_COLOR;
        break;
      }
      case 'priority': {
        const priorityConfig = PRIORITY_CONFIG.find(p => p.id === task.priority);
        key = task.priority || '__none__';
        label = priorityConfig?.name || 'No Priority';
        color = priorityConfig?.color || DEFAULT_COLOR;
        break;
      }
      case 'tag': {
        const tagName = getPrimaryTag(task);
        const tagConfig = tagName ? allTags.find(t => t.name === tagName) : null;
        key = tagName || '__none__';
        label = tagName || 'No Tag';
        color = tagConfig?.color || DEFAULT_COLOR;
        break;
      }
      case 'difficulty': {
        const difficultyLabels = { 
          very_easy: 'Very Easy', 
          easy: 'Easy', 
          medium: 'Medium', 
          hard: 'Hard', 
          extreme: 'Extreme' 
        };
        const difficultyColors = { 
          very_easy: '#22c55e', 
          easy: '#84cc16', 
          medium: '#f59e0b', 
          hard: '#f97316', 
          extreme: '#ef4444' 
        };
        key = task.difficulty || '__none__';
        label = difficultyLabels[task.difficulty] || 'No Difficulty';
        color = difficultyColors[task.difficulty] || DEFAULT_COLOR;
        break;
      }
      default:
        key = '__all__';
        label = 'All Tasks';
        color = DEFAULT_COLOR;
    }
    
    if (!groups.has(key)) {
      groups.set(key, { key, label, color, tasks: [] });
    }
    groups.get(key).tasks.push(task);
  });
  
  // Convert to array and sort groups
  const groupArray = Array.from(groups.values());
  
  // Sort groups based on swimlaneSetting
  if (swimlaneSetting === 'status') {
    // Sort by status order
    const statusOrder = statuses.map(s => s.id);
    groupArray.sort((a, b) => {
      const aIndex = statusOrder.indexOf(a.key);
      const bIndex = statusOrder.indexOf(b.key);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  } else if (swimlaneSetting === 'priority') {
    // Sort by priority order (high -> medium -> low -> none)
    const priorityOrder = ['high', 'medium', 'low', '__none__'];
    groupArray.sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a.key);
      const bIndex = priorityOrder.indexOf(b.key);
      return aIndex - bIndex;
    });
  } else if (swimlaneSetting === 'tag') {
    // Sort alphabetically, with "No Tag" last
    groupArray.sort((a, b) => {
      if (a.key === '__none__') return 1;
      if (b.key === '__none__') return -1;
      return a.label.localeCompare(b.label);
    });
  } else if (swimlaneSetting === 'difficulty') {
    // Sort by difficulty order (very_easy -> easy -> medium -> hard -> extreme -> none)
    const difficultyOrder = ['very_easy', 'easy', 'medium', 'hard', 'extreme', '__none__'];
    groupArray.sort((a, b) => {
      const aIndex = difficultyOrder.indexOf(a.key);
      const bIndex = difficultyOrder.indexOf(b.key);
      return aIndex - bIndex;
    });
  }
  
  // Apply sort setting to tasks within each group
  if (sortSetting && sortSetting !== 'manual') {
    groupArray.forEach(group => {
      group.tasks = sortTasks(group.tasks, sortSetting, customFields);
    });
  }
  
  return groupArray;
};
